import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  DoorOpen, Archive, Square, Minus, Grid3X3, 
  Trash2, Copy, Move, ZoomIn, ZoomOut, RotateCcw,
  Maximize2, SplitSquareVertical, GripVertical,
  MousePointer2, Hand, Grid, Layers, PanelTop,
  LayoutGrid, Blinds, GlassWater
} from 'lucide-react';

// Component types with colors and styles - SERRARIA GUEDES STYLE
const COMPONENT_TYPES = {
  porta_ripada: { 
    label: 'Porta Ripada', 
    color: '#B8860B', 
    strokeColor: '#8B6914',
    icon: Blinds,
    defaultFerragens: ['dobradiça', 'puxador']
  },
  porta_lisa: { 
    label: 'Porta Lisa', 
    color: '#CD853F', 
    strokeColor: '#A0522D',
    icon: DoorOpen,
    defaultFerragens: ['dobradiça', 'puxador']
  },
  porta_vidro: { 
    label: 'Porta Vidro', 
    color: '#8B7355', 
    strokeColor: '#6B5344',
    icon: GlassWater,
    defaultFerragens: ['dobradiça', 'puxador', 'vidro']
  },
  gaveta: { 
    label: 'Gaveta', 
    color: '#DAA520', 
    strokeColor: '#B8860B',
    icon: Archive,
    defaultFerragens: ['corrediça', 'puxador']
  },
  gaveta_ripada: { 
    label: 'Gaveta Ripada', 
    color: '#CD853F', 
    strokeColor: '#A0522D',
    icon: Archive,
    defaultFerragens: ['corrediça', 'puxador']
  },
  nicho: { 
    label: 'Nicho Aberto', 
    color: '#4A3A20', 
    strokeColor: '#352A18',
    icon: Square,
    defaultFerragens: []
  },
  prateleira: { 
    label: 'Prateleira', 
    color: '#8B7355', 
    strokeColor: '#5D4D3A',
    icon: Minus,
    defaultFerragens: ['suporte']
  },
  divisoria: { 
    label: 'Divisória', 
    color: '#6B5344', 
    strokeColor: '#483830',
    icon: Grid3X3,
    defaultFerragens: []
  },
  tampo: { 
    label: 'Tampo/Bancada', 
    color: '#2F2F2F', 
    strokeColor: '#1a1a1a',
    icon: PanelTop,
    defaultFerragens: []
  },
};

// Adjust color brightness
const adjustBrightness = (hex, percent) => {
  if (hex.startsWith('rgba')) return hex;
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

export default function Editor2D({ 
  largura = 200, 
  altura = 200, 
  profundidade = 50,
  componentes = [],
  onComponentesChange,
  readOnly = false 
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // View state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Selection state
  const [selectedId, setSelectedId] = useState(null);
  const [tool, setTool] = useState('select');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  
  // Grid settings
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const GRID_SIZE = 5; // cm - smaller for more precision
  
  // Canvas dimensions
  const CANVAS_WIDTH = 900;
  const CANVAS_HEIGHT = 550;
  
  // Calculate auto scale
  const getAutoScale = useCallback(() => {
    const padding = 120;
    const availableWidth = CANVAS_WIDTH - padding * 2;
    const availableHeight = CANVAS_HEIGHT - padding * 2;
    const scaleX = availableWidth / largura;
    const scaleY = availableHeight / altura;
    return Math.min(scaleX, scaleY, 3);
  }, [largura, altura]);
  
  const getCurrentScale = useCallback(() => {
    return getAutoScale() * scale;
  }, [getAutoScale, scale]);
  
  const toCanvas = useCallback((x, y) => {
    const s = getCurrentScale();
    const centerX = CANVAS_WIDTH / 2 + offset.x;
    const centerY = CANVAS_HEIGHT / 2 + offset.y;
    return {
      x: centerX - (largura * s) / 2 + x * s,
      y: centerY - (altura * s) / 2 + y * s
    };
  }, [getCurrentScale, largura, altura, offset]);
  
  const toReal = useCallback((canvasX, canvasY) => {
    const s = getCurrentScale();
    const centerX = CANVAS_WIDTH / 2 + offset.x;
    const centerY = CANVAS_HEIGHT / 2 + offset.y;
    const startX = centerX - (largura * s) / 2;
    const startY = centerY - (altura * s) / 2;
    
    let realX = (canvasX - startX) / s;
    let realY = (canvasY - startY) / s;
    
    if (snapToGrid) {
      realX = Math.round(realX / GRID_SIZE) * GRID_SIZE;
      realY = Math.round(realY / GRID_SIZE) * GRID_SIZE;
    }
    
    return {
      x: Math.max(0, Math.min(largura, realX)),
      y: Math.max(0, Math.min(altura, realY))
    };
  }, [getCurrentScale, largura, altura, offset, snapToGrid]);
  
  // Draw wood grain texture
  const drawWoodGrain = (ctx, x, y, w, h, baseColor, vertical = true) => {
    // Base color
    const gradient = vertical 
      ? ctx.createLinearGradient(x, y, x + w, y)
      : ctx.createLinearGradient(x, y, x, y + h);
    
    gradient.addColorStop(0, baseColor);
    gradient.addColorStop(0.15, adjustBrightness(baseColor, 12));
    gradient.addColorStop(0.3, baseColor);
    gradient.addColorStop(0.5, adjustBrightness(baseColor, 8));
    gradient.addColorStop(0.7, adjustBrightness(baseColor, -5));
    gradient.addColorStop(0.85, baseColor);
    gradient.addColorStop(1, adjustBrightness(baseColor, -8));
    
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, w, h);
    
    // Wood grain lines
    ctx.strokeStyle = adjustBrightness(baseColor, -15);
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.3;
    
    const lineSpacing = vertical ? w / 8 : h / 8;
    const numLines = vertical ? Math.floor(w / lineSpacing) : Math.floor(h / lineSpacing);
    
    for (let i = 0; i < numLines; i++) {
      const offset = (Math.random() - 0.5) * 3;
      ctx.beginPath();
      if (vertical) {
        const lineX = x + i * lineSpacing + offset;
        ctx.moveTo(lineX, y);
        // Wavy line for natural wood grain
        for (let py = y; py < y + h; py += 10) {
          ctx.lineTo(lineX + Math.sin(py * 0.05) * 2, py);
        }
      } else {
        const lineY = y + i * lineSpacing + offset;
        ctx.moveTo(x, lineY);
        for (let px = x; px < x + w; px += 10) {
          ctx.lineTo(px, lineY + Math.sin(px * 0.05) * 2);
        }
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  };
  
  // Draw slats (ripado)
  const drawSlats = (ctx, x, y, w, h, baseColor, numSlats = null) => {
    const slatCount = numSlats || Math.max(3, Math.floor(w / 8));
    const slatWidth = (w - 4) / slatCount;
    const gap = 2;
    const actualSlatWidth = slatWidth - gap;
    
    // Background (gap color)
    ctx.fillStyle = adjustBrightness(baseColor, -40);
    ctx.fillRect(x + 2, y + 2, w - 4, h - 4);
    
    // Draw each slat
    for (let i = 0; i < slatCount; i++) {
      const slatX = x + 2 + i * slatWidth + gap/2;
      
      // Slat with wood grain
      const slatGradient = ctx.createLinearGradient(slatX, y, slatX + actualSlatWidth, y);
      slatGradient.addColorStop(0, adjustBrightness(baseColor, 5));
      slatGradient.addColorStop(0.3, adjustBrightness(baseColor, 15));
      slatGradient.addColorStop(0.5, baseColor);
      slatGradient.addColorStop(0.7, adjustBrightness(baseColor, 10));
      slatGradient.addColorStop(1, adjustBrightness(baseColor, -5));
      
      ctx.fillStyle = slatGradient;
      ctx.fillRect(slatX, y + 4, actualSlatWidth, h - 8);
      
      // Slat border
      ctx.strokeStyle = adjustBrightness(baseColor, -20);
      ctx.lineWidth = 0.5;
      ctx.strokeRect(slatX, y + 4, actualSlatWidth, h - 8);
      
      // Highlight on left edge
      ctx.strokeStyle = adjustBrightness(baseColor, 25);
      ctx.beginPath();
      ctx.moveTo(slatX + 1, y + 5);
      ctx.lineTo(slatX + 1, y + h - 5);
      ctx.stroke();
    }
  };
  
  // Draw handle (puxador)
  const drawHandle = (ctx, x, y, w, h, horizontal = true, style = 'rect') => {
    ctx.save();
    
    if (style === 'rect') {
      // Rectangular handle like in the images
      const handleW = horizontal ? Math.min(40, w * 0.3) : 8;
      const handleH = horizontal ? 8 : Math.min(40, h * 0.25);
      const handleX = x + (horizontal ? (w - handleW) / 2 : w - 15);
      const handleY = y + (horizontal ? h - 20 : (h - handleH) / 2);
      
      // Handle shadow
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(handleX + 2, handleY + 2, handleW, handleH);
      
      // Handle body
      const handleGradient = ctx.createLinearGradient(handleX, handleY, handleX + handleW, handleY + handleH);
      handleGradient.addColorStop(0, '#8B7355');
      handleGradient.addColorStop(0.3, '#A0896B');
      handleGradient.addColorStop(0.5, '#8B7355');
      handleGradient.addColorStop(1, '#6B5344');
      
      ctx.fillStyle = handleGradient;
      ctx.beginPath();
      ctx.roundRect(handleX, handleY, handleW, handleH, 2);
      ctx.fill();
      
      // Handle border
      ctx.strokeStyle = '#5D4037';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Highlight
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.beginPath();
      ctx.moveTo(handleX + 2, handleY + 1);
      ctx.lineTo(handleX + handleW - 2, handleY + 1);
      ctx.stroke();
    }
    
    ctx.restore();
  };
  
  // Draw glass panel
  const drawGlass = (ctx, x, y, w, h, frameWidth = 6) => {
    // Outer frame
    ctx.fillStyle = '#8B7355';
    ctx.fillRect(x, y, w, h);
    
    // Frame gradient
    const frameGradient = ctx.createLinearGradient(x, y, x + w, y + h);
    frameGradient.addColorStop(0, '#9B8465');
    frameGradient.addColorStop(0.5, '#8B7355');
    frameGradient.addColorStop(1, '#7B6345');
    ctx.fillStyle = frameGradient;
    ctx.fillRect(x, y, w, h);
    
    // Inner glass area
    const glassX = x + frameWidth;
    const glassY = y + frameWidth;
    const glassW = w - frameWidth * 2;
    const glassH = h - frameWidth * 2;
    
    // Glass background (dark to show depth)
    ctx.fillStyle = 'rgba(30, 25, 20, 0.8)';
    ctx.fillRect(glassX, glassY, glassW, glassH);
    
    // Glass reflection
    ctx.fillStyle = 'rgba(135, 206, 235, 0.15)';
    ctx.fillRect(glassX, glassY, glassW, glassH);
    
    // Reflection shine
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.moveTo(glassX + 3, glassY + 3);
    ctx.lineTo(glassX + glassW * 0.4, glassY + 3);
    ctx.lineTo(glassX + 3, glassY + glassH * 0.4);
    ctx.closePath();
    ctx.fill();
    
    // Inner frame border
    ctx.strokeStyle = '#5D4037';
    ctx.lineWidth = 1;
    ctx.strokeRect(glassX, glassY, glassW, glassH);
  };
  
  // Draw granite/stone countertop
  const drawCountertop = (ctx, x, y, w, h) => {
    // Base dark color
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(x, y, w, h);
    
    // Granite speckles
    for (let i = 0; i < w * h / 20; i++) {
      const px = x + Math.random() * w;
      const py = y + Math.random() * h;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 60 + 30;
      ctx.fillStyle = `rgb(${brightness}, ${brightness}, ${brightness})`;
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Gold/brown flecks
    for (let i = 0; i < w * h / 100; i++) {
      const px = x + Math.random() * w;
      const py = y + Math.random() * h;
      ctx.fillStyle = `rgba(139, 115, 85, ${Math.random() * 0.3})`;
      ctx.beginPath();
      ctx.arc(px, py, Math.random() * 1.5 + 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Top edge highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(x, y, w, 2);
    
    // Front edge
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(x, y + h - 3, w, 3);
  };
  
  // Main draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const s = getCurrentScale();
    const { x: startX, y: startY } = toCanvas(0, 0);
    const width = largura * s;
    const height = altura * s;
    
    // Background gradient
    const bgGradient = ctx.createRadialGradient(
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 0,
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH
    );
    bgGradient.addColorStop(0, '#1a1a1a');
    bgGradient.addColorStop(1, '#0a0a0a');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Grid
    if (showGrid) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.lineWidth = 1;
      
      const gridStep = GRID_SIZE * s;
      for (let x = startX % gridStep; x < CANVAS_WIDTH; x += gridStep) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, CANVAS_HEIGHT);
        ctx.stroke();
      }
      for (let y = startY % gridStep; y < CANVAS_HEIGHT; y += gridStep) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(CANVAS_WIDTH, y);
        ctx.stroke();
      }
      
      // Major grid (50cm)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      const majorStep = 50 * s;
      for (let x = startX; x < startX + width; x += majorStep) {
        ctx.beginPath();
        ctx.moveTo(x, startY);
        ctx.lineTo(x, startY + height);
        ctx.stroke();
      }
      for (let y = startY; y < startY + height; y += majorStep) {
        ctx.beginPath();
        ctx.moveTo(startX, y);
        ctx.lineTo(startX + width, y);
        ctx.stroke();
      }
    }
    
    // Furniture shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetX = 15;
    ctx.shadowOffsetY = 15;
    
    // Furniture body
    drawWoodGrain(ctx, startX, startY, width, height, '#8B7355');
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // Furniture border
    ctx.strokeStyle = '#5D4037';
    ctx.lineWidth = 4;
    ctx.strokeRect(startX, startY, width, height);
    
    // Inner border
    ctx.strokeStyle = '#6B5344';
    ctx.lineWidth = 1;
    ctx.strokeRect(startX + 3, startY + 3, width - 6, height - 6);
    
    // Draw components
    componentes.forEach((comp) => {
      const compX = startX + comp.posicao_x * s;
      const compY = startY + comp.posicao_y * s;
      const compW = comp.largura * s;
      const compH = comp.altura * s;
      
      const isSelected = comp.id === selectedId;
      const typeConfig = COMPONENT_TYPES[comp.tipo] || COMPONENT_TYPES.porta_lisa;
      
      // Component shadow
      if (!isSelected) {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
      }
      
      // Draw based on type
      switch (comp.tipo) {
        case 'porta_ripada':
          drawSlats(ctx, compX, compY, compW, compH, typeConfig.color);
          drawHandle(ctx, compX, compY, compW, compH, false, 'rect');
          break;
          
        case 'porta_lisa':
          drawWoodGrain(ctx, compX, compY, compW, compH, typeConfig.color);
          // Panel effect
          ctx.strokeStyle = adjustBrightness(typeConfig.color, -20);
          ctx.lineWidth = 1;
          ctx.strokeRect(compX + 6, compY + 6, compW - 12, compH - 12);
          drawHandle(ctx, compX, compY, compW, compH, false, 'rect');
          break;
          
        case 'porta_vidro':
          drawGlass(ctx, compX, compY, compW, compH, 8);
          drawHandle(ctx, compX, compY, compW, compH, false, 'rect');
          break;
          
        case 'gaveta':
          drawWoodGrain(ctx, compX, compY, compW, compH, typeConfig.color);
          // Drawer lines
          ctx.strokeStyle = adjustBrightness(typeConfig.color, -25);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(compX + 8, compY + compH * 0.3);
          ctx.lineTo(compX + compW - 8, compY + compH * 0.3);
          ctx.moveTo(compX + 8, compY + compH * 0.7);
          ctx.lineTo(compX + compW - 8, compY + compH * 0.7);
          ctx.stroke();
          drawHandle(ctx, compX, compY, compW, compH, true, 'rect');
          break;
          
        case 'gaveta_ripada':
          drawSlats(ctx, compX, compY, compW, compH, typeConfig.color);
          drawHandle(ctx, compX, compY, compW, compH, true, 'rect');
          break;
          
        case 'nicho':
          // Depth effect
          ctx.fillStyle = 'rgba(20, 15, 10, 0.9)';
          ctx.fillRect(compX, compY, compW, compH);
          // Inner shadow
          const nicho_gradient = ctx.createLinearGradient(compX, compY, compX, compY + compH);
          nicho_gradient.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
          nicho_gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.2)');
          nicho_gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');
          ctx.fillStyle = nicho_gradient;
          ctx.fillRect(compX + 3, compY + 3, compW - 6, compH - 6);
          // Border
          ctx.strokeStyle = '#4A3A20';
          ctx.lineWidth = 2;
          ctx.strokeRect(compX, compY, compW, compH);
          break;
          
        case 'prateleira':
          const shelfH = Math.min(6, compH);
          drawWoodGrain(ctx, compX, compY + compH/2 - shelfH/2, compW, shelfH, typeConfig.color, false);
          ctx.strokeStyle = '#5D4037';
          ctx.lineWidth = 1;
          ctx.strokeRect(compX, compY + compH/2 - shelfH/2, compW, shelfH);
          break;
          
        case 'divisoria':
          const divW = Math.min(6, compW);
          drawWoodGrain(ctx, compX + compW/2 - divW/2, compY, divW, compH, typeConfig.color);
          ctx.strokeStyle = '#5D4037';
          ctx.lineWidth = 1;
          ctx.strokeRect(compX + compW/2 - divW/2, compY, divW, compH);
          break;
          
        case 'tampo':
          drawCountertop(ctx, compX, compY, compW, compH);
          break;
          
        default:
          drawWoodGrain(ctx, compX, compY, compW, compH, typeConfig.color);
      }
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      
      // Component border
      ctx.strokeStyle = adjustBrightness(typeConfig.strokeColor, -10);
      ctx.lineWidth = 2;
      ctx.strokeRect(compX, compY, compW, compH);
      
      // Selection
      if (isSelected && !readOnly) {
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(compX - 2, compY - 2, compW + 4, compH + 4);
        ctx.setLineDash([]);
        
        // Handles
        const handleSize = 10;
        ctx.fillStyle = '#FFD700';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        
        const handles = [
          { x: compX - handleSize/2, y: compY - handleSize/2 },
          { x: compX + compW - handleSize/2, y: compY - handleSize/2 },
          { x: compX - handleSize/2, y: compY + compH - handleSize/2 },
          { x: compX + compW - handleSize/2, y: compY + compH - handleSize/2 },
        ];
        
        handles.forEach(h => {
          ctx.fillRect(h.x, h.y, handleSize, handleSize);
          ctx.strokeRect(h.x, h.y, handleSize, handleSize);
        });
      }
      
      // Dimensions
      if (isSelected || componentes.length <= 5) {
        const text = `${Math.round(comp.largura)}×${Math.round(comp.altura)}`;
        ctx.font = `${isSelected ? 'bold ' : ''}10px "Segoe UI", Arial`;
        const textWidth = ctx.measureText(text).width;
        
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(compX + compW/2 - textWidth/2 - 4, compY + compH/2 - 7, textWidth + 8, 14);
        
        ctx.fillStyle = isSelected ? '#FFD700' : '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, compX + compW/2, compY + compH/2);
      }
    });
    
    // Dimension lines
    drawDimensionLines(ctx, startX, startY, width, height, s);
    
    // Tool indicator
    if (tool !== 'select' && tool !== 'pan' && !readOnly) {
      ctx.fillStyle = 'rgba(255, 215, 0, 0.95)';
      ctx.font = 'bold 12px "Segoe UI", Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`+ ${COMPONENT_TYPES[tool]?.label || tool}`, 15, CANVAS_HEIGHT - 15);
    }
    
  }, [componentes, selectedId, largura, altura, profundidade, getCurrentScale, toCanvas, tool, readOnly, showGrid]);
  
  // Dimension lines
  const drawDimensionLines = (ctx, startX, startY, width, height, scale) => {
    const lineOffset = 35;
    const tickSize = 8;
    
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 1;
    ctx.font = '11px "Segoe UI", Arial';
    
    // Bottom (width)
    const bottomY = startY + height + lineOffset;
    ctx.beginPath();
    ctx.moveTo(startX, startY + height + 5);
    ctx.lineTo(startX, bottomY + tickSize);
    ctx.moveTo(startX + width, startY + height + 5);
    ctx.lineTo(startX + width, bottomY + tickSize);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(startX, bottomY);
    ctx.lineTo(startX + width, bottomY);
    ctx.stroke();
    
    drawArrow(ctx, startX, bottomY, 'right');
    drawArrow(ctx, startX + width, bottomY, 'left');
    
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${largura} cm`, startX + width/2, bottomY + 5);
    
    // Left (height)
    const leftX = startX - lineOffset;
    ctx.beginPath();
    ctx.moveTo(startX - 5, startY);
    ctx.lineTo(leftX - tickSize, startY);
    ctx.moveTo(startX - 5, startY + height);
    ctx.lineTo(leftX - tickSize, startY + height);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(leftX, startY);
    ctx.lineTo(leftX, startY + height);
    ctx.stroke();
    
    drawArrow(ctx, leftX, startY, 'down');
    drawArrow(ctx, leftX, startY + height, 'up');
    
    ctx.save();
    ctx.translate(leftX - 12, startY + height/2);
    ctx.rotate(-Math.PI/2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${altura} cm`, 0, 0);
    ctx.restore();
    
    // Depth
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText(`Prof: ${profundidade} cm`, startX + width, startY - 10);
  };
  
  const drawArrow = (ctx, x, y, direction) => {
    const size = 6;
    ctx.beginPath();
    switch (direction) {
      case 'right':
        ctx.moveTo(x, y);
        ctx.lineTo(x + size, y - size/2);
        ctx.lineTo(x + size, y + size/2);
        break;
      case 'left':
        ctx.moveTo(x, y);
        ctx.lineTo(x - size, y - size/2);
        ctx.lineTo(x - size, y + size/2);
        break;
      case 'up':
        ctx.moveTo(x, y);
        ctx.lineTo(x - size/2, y - size);
        ctx.lineTo(x + size/2, y - size);
        break;
      case 'down':
        ctx.moveTo(x, y);
        ctx.lineTo(x - size/2, y + size);
        ctx.lineTo(x + size/2, y + size);
        break;
    }
    ctx.closePath();
    ctx.fill();
  };
  
  useEffect(() => {
    draw();
  }, [draw, scale, offset]);
  
  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);
  
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.3, Math.min(3, prev * delta)));
  }, []);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvas.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);
  
  const findComponentAt = useCallback((canvasX, canvasY) => {
    const s = getCurrentScale();
    const { x: startX, y: startY } = toCanvas(0, 0);
    
    for (let i = componentes.length - 1; i >= 0; i--) {
      const comp = componentes[i];
      const compX = startX + comp.posicao_x * s;
      const compY = startY + comp.posicao_y * s;
      const compW = comp.largura * s;
      const compH = comp.altura * s;
      
      if (canvasX >= compX && canvasX <= compX + compW &&
          canvasY >= compY && canvasY <= compY + compH) {
        return comp;
      }
    }
    return null;
  }, [componentes, getCurrentScale, toCanvas]);
  
  const findResizeHandle = useCallback((canvasX, canvasY) => {
    if (!selectedId) return null;
    
    const comp = componentes.find(c => c.id === selectedId);
    if (!comp) return null;
    
    const s = getCurrentScale();
    const { x: startX, y: startY } = toCanvas(0, 0);
    const compX = startX + comp.posicao_x * s;
    const compY = startY + comp.posicao_y * s;
    const compW = comp.largura * s;
    const compH = comp.altura * s;
    
    const handleSize = 15;
    const handles = [
      { name: 'nw', x: compX, y: compY },
      { name: 'ne', x: compX + compW, y: compY },
      { name: 'sw', x: compX, y: compY + compH },
      { name: 'se', x: compX + compW, y: compY + compH },
    ];
    
    for (const handle of handles) {
      if (Math.abs(canvasX - handle.x) < handleSize && Math.abs(canvasY - handle.y) < handleSize) {
        return handle.name;
      }
    }
    return null;
  }, [selectedId, componentes, getCurrentScale, toCanvas]);
  
  const handleMouseDown = useCallback((e) => {
    if (readOnly) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    if (e.button === 1 || tool === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      return;
    }
    
    const handle = findResizeHandle(canvasX, canvasY);
    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart({ x: canvasX, y: canvasY });
      return;
    }
    
    if (tool === 'select') {
      const comp = findComponentAt(canvasX, canvasY);
      if (comp) {
        setSelectedId(comp.id);
        setIsDragging(true);
        setDragStart({ x: canvasX, y: canvasY });
      } else {
        setSelectedId(null);
      }
    } else if (COMPONENT_TYPES[tool]) {
      const real = toReal(canvasX, canvasY);
      let defaultWidth, defaultHeight;
      
      switch (tool) {
        case 'prateleira':
          defaultWidth = Math.min(largura * 0.8, largura - real.x);
          defaultHeight = 4;
          break;
        case 'divisoria':
          defaultWidth = 4;
          defaultHeight = Math.min(altura * 0.8, altura - real.y);
          break;
        case 'tampo':
          defaultWidth = Math.min(largura * 0.9, largura - real.x);
          defaultHeight = 6;
          break;
        case 'gaveta':
        case 'gaveta_ripada':
          defaultWidth = Math.min(60, largura * 0.3);
          defaultHeight = Math.min(25, altura * 0.15);
          break;
        default:
          defaultWidth = Math.min(50, largura * 0.25);
          defaultHeight = Math.min(80, altura * 0.4);
      }
      
      const newComp = {
        id: `comp_${Date.now()}`,
        tipo: tool,
        largura: defaultWidth,
        altura: defaultHeight,
        posicao_x: Math.max(0, Math.min(largura - defaultWidth, real.x - defaultWidth/2)),
        posicao_y: Math.max(0, Math.min(altura - defaultHeight, real.y - defaultHeight/2)),
        quantidade: 1,
        ferragens: COMPONENT_TYPES[tool]?.defaultFerragens || []
      };
      
      onComponentesChange([...componentes, newComp]);
      setSelectedId(newComp.id);
      setTool('select');
    }
  }, [tool, findComponentAt, findResizeHandle, toReal, componentes, onComponentesChange, largura, altura, readOnly, offset]);
  
  const handleMouseMove = useCallback((e) => {
    if (readOnly) return;
    
    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
      return;
    }
    
    if (!isDragging && !isResizing) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    const deltaX = canvasX - dragStart.x;
    const deltaY = canvasY - dragStart.y;
    const s = getCurrentScale();
    
    if (isResizing && selectedId) {
      const comp = componentes.find(c => c.id === selectedId);
      if (!comp) return;
      
      let newLargura = comp.largura;
      let newAltura = comp.altura;
      let newX = comp.posicao_x;
      let newY = comp.posicao_y;
      
      let realDeltaX = deltaX / s;
      let realDeltaY = deltaY / s;
      
      if (snapToGrid) {
        realDeltaX = Math.round(realDeltaX / GRID_SIZE) * GRID_SIZE;
        realDeltaY = Math.round(realDeltaY / GRID_SIZE) * GRID_SIZE;
      }
      
      if (resizeHandle.includes('e')) newLargura = Math.max(10, comp.largura + realDeltaX);
      if (resizeHandle.includes('w')) {
        const widthChange = Math.min(comp.largura - 10, realDeltaX);
        newLargura = comp.largura - widthChange;
        newX = comp.posicao_x + widthChange;
      }
      if (resizeHandle.includes('s')) newAltura = Math.max(10, comp.altura + realDeltaY);
      if (resizeHandle.includes('n')) {
        const heightChange = Math.min(comp.altura - 10, realDeltaY);
        newAltura = comp.altura - heightChange;
        newY = comp.posicao_y + heightChange;
      }
      
      newX = Math.max(0, newX);
      newY = Math.max(0, newY);
      newLargura = Math.min(newLargura, largura - newX);
      newAltura = Math.min(newAltura, altura - newY);
      
      const newComponentes = componentes.map(c => 
        c.id === selectedId 
          ? { ...c, largura: newLargura, altura: newAltura, posicao_x: newX, posicao_y: newY }
          : c
      );
      onComponentesChange(newComponentes);
      setDragStart({ x: canvasX, y: canvasY });
      
    } else if (isDragging && selectedId) {
      const comp = componentes.find(c => c.id === selectedId);
      if (!comp) return;
      
      let realDeltaX = deltaX / s;
      let realDeltaY = deltaY / s;
      
      if (snapToGrid) {
        realDeltaX = Math.round(realDeltaX / GRID_SIZE) * GRID_SIZE;
        realDeltaY = Math.round(realDeltaY / GRID_SIZE) * GRID_SIZE;
      }
      
      let newX = Math.max(0, Math.min(largura - comp.largura, comp.posicao_x + realDeltaX));
      let newY = Math.max(0, Math.min(altura - comp.altura, comp.posicao_y + realDeltaY));
      
      const newComponentes = componentes.map(c => 
        c.id === selectedId ? { ...c, posicao_x: newX, posicao_y: newY } : c
      );
      onComponentesChange(newComponentes);
      setDragStart({ x: canvasX, y: canvasY });
    }
  }, [isDragging, isResizing, isPanning, dragStart, panStart, selectedId, componentes, onComponentesChange, getCurrentScale, resizeHandle, largura, altura, readOnly, snapToGrid]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setIsPanning(false);
    setResizeHandle(null);
  }, []);
  
  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    onComponentesChange(componentes.filter(c => c.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, componentes, onComponentesChange]);
  
  const duplicateSelected = useCallback(() => {
    if (!selectedId) return;
    const comp = componentes.find(c => c.id === selectedId);
    if (!comp) return;
    
    const newComp = {
      ...comp,
      id: `comp_${Date.now()}`,
      posicao_x: Math.min(comp.posicao_x + 15, largura - comp.largura),
      posicao_y: Math.min(comp.posicao_y + 15, altura - comp.altura),
    };
    
    onComponentesChange([...componentes, newComp]);
    setSelectedId(newComp.id);
  }, [selectedId, componentes, onComponentesChange, largura, altura]);
  
  const dividirIgualmente = useCallback((divisoes, tipo = 'porta_ripada') => {
    const espacamento = 4;
    const compLargura = (largura - espacamento * (divisoes + 1)) / divisoes;
    
    const newComponentes = [];
    for (let i = 0; i < divisoes; i++) {
      newComponentes.push({
        id: `comp_${Date.now()}_${i}`,
        tipo: tipo,
        largura: compLargura,
        altura: altura - espacamento * 2,
        posicao_x: espacamento + i * (compLargura + espacamento),
        posicao_y: espacamento,
        quantidade: 1,
        ferragens: COMPONENT_TYPES[tipo]?.defaultFerragens || []
      });
    }
    
    onComponentesChange(newComponentes);
    setSelectedId(null);
  }, [largura, altura, onComponentesChange]);
  
  const clearAll = useCallback(() => {
    onComponentesChange([]);
    setSelectedId(null);
  }, [onComponentesChange]);
  
  const updateSelectedDimensions = useCallback((field, value) => {
    if (!selectedId) return;
    const numValue = parseFloat(value) || 0;
    
    const newComponentes = componentes.map(c => {
      if (c.id !== selectedId) return c;
      
      let updated = { ...c, [field]: numValue };
      
      if (field === 'largura') updated.largura = Math.min(numValue, largura - c.posicao_x);
      else if (field === 'altura') updated.altura = Math.min(numValue, altura - c.posicao_y);
      else if (field === 'posicao_x') updated.posicao_x = Math.max(0, Math.min(numValue, largura - c.largura));
      else if (field === 'posicao_y') updated.posicao_y = Math.max(0, Math.min(numValue, altura - c.altura));
      
      return updated;
    });
    
    onComponentesChange(newComponentes);
  }, [selectedId, componentes, onComponentesChange, largura, altura]);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (readOnly) return;
      
      if ((e.key === 'Delete' || e.key === 'Backspace') && document.activeElement.tagName !== 'INPUT') {
        e.preventDefault();
        deleteSelected();
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        setTool('select');
      }
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, deleteSelected, duplicateSelected, readOnly]);
  
  const selectedComponent = componentes.find(c => c.id === selectedId);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 p-2 bg-zinc-900/90 rounded-lg border border-zinc-800">
          {/* Selection */}
          <div className="flex items-center gap-1 pr-2 border-r border-zinc-700">
            <Button size="sm" variant={tool === 'select' ? 'default' : 'ghost'} onClick={() => setTool('select')} title="Selecionar" className="h-8 w-8 p-0">
              <MousePointer2 className="h-4 w-4" />
            </Button>
            <Button size="sm" variant={tool === 'pan' ? 'default' : 'ghost'} onClick={() => setTool('pan')} title="Mover Vista" className="h-8 w-8 p-0">
              <Hand className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Components */}
          <div className="flex items-center gap-1 px-2 border-r border-zinc-700">
            {Object.entries(COMPONENT_TYPES).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <Button key={key} size="sm" variant={tool === key ? 'default' : 'ghost'} onClick={() => setTool(key)} title={config.label} className="h-8 w-8 p-0">
                  <Icon className="h-4 w-4" />
                </Button>
              );
            })}
          </div>
          
          {/* Dividers */}
          <div className="flex items-center gap-1 px-2 border-r border-zinc-700">
            <Button size="sm" variant="ghost" onClick={() => dividirIgualmente(2, 'porta_ripada')} title="Dividir 2" className="h-8 px-2">
              <SplitSquareVertical className="h-4 w-4 mr-1" /><span className="text-xs">2</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => dividirIgualmente(3, 'porta_ripada')} title="Dividir 3" className="h-8 px-2">
              <SplitSquareVertical className="h-4 w-4 mr-1" /><span className="text-xs">3</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => dividirIgualmente(4, 'porta_ripada')} title="Dividir 4" className="h-8 px-2">
              <SplitSquareVertical className="h-4 w-4 mr-1" /><span className="text-xs">4</span>
            </Button>
          </div>
          
          {/* Zoom */}
          <div className="flex items-center gap-1 px-2 border-r border-zinc-700">
            <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.min(3, s * 1.2))} title="Zoom +" className="h-8 w-8 p-0">
              <ZoomIn className="h-4 w-4" />
            </Button>
            <span className="text-xs text-zinc-400 w-12 text-center">{Math.round(scale * 100)}%</span>
            <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.max(0.3, s * 0.8))} title="Zoom -" className="h-8 w-8 p-0">
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={resetView} title="Reset" className="h-8 w-8 p-0">
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Grid */}
          <div className="flex items-center gap-1 px-2 border-r border-zinc-700">
            <Button size="sm" variant={showGrid ? 'default' : 'ghost'} onClick={() => setShowGrid(!showGrid)} title="Grid" className="h-8 w-8 p-0">
              <Grid className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 pl-2">
            {selectedId && (
              <>
                <Button size="sm" variant="ghost" onClick={duplicateSelected} title="Duplicar" className="h-8 w-8 p-0">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={deleteSelected} title="Excluir" className="h-8 w-8 p-0 text-red-400">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={clearAll} title="Limpar" className="h-8 w-8 p-0 text-red-400">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="ml-auto">
            <Badge variant="outline" className="bg-zinc-800 border-zinc-700">
              <Layers className="h-3 w-3 mr-1" />{componentes.length}
            </Badge>
          </div>
        </div>
      )}
      
      {/* Canvas */}
      <div ref={containerRef} className="relative rounded-lg overflow-hidden border border-zinc-800 shadow-2xl" style={{ height: readOnly ? '220px' : '550px' }}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="w-full h-full"
          style={{ cursor: tool === 'pan' ? (isPanning ? 'grabbing' : 'grab') : tool === 'select' ? (isDragging ? 'grabbing' : 'default') : 'crosshair' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>
      
      {/* Properties */}
      {selectedComponent && !readOnly && (
        <div className="p-3 bg-zinc-900/90 rounded-lg border border-zinc-800 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-zinc-200">{COMPONENT_TYPES[selectedComponent.tipo]?.label}</span>
            <Badge variant="outline" className="bg-zinc-800 border-zinc-700 text-xs">{selectedComponent.tipo}</Badge>
          </div>
          
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Largura</Label>
              <Input type="number" value={Math.round(selectedComponent.largura)} onChange={(e) => updateSelectedDimensions('largura', e.target.value)} className="h-8 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Altura</Label>
              <Input type="number" value={Math.round(selectedComponent.altura)} onChange={(e) => updateSelectedDimensions('altura', e.target.value)} className="h-8 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Pos. X</Label>
              <Input type="number" value={Math.round(selectedComponent.posicao_x)} onChange={(e) => updateSelectedDimensions('posicao_x', e.target.value)} className="h-8 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Pos. Y</Label>
              <Input type="number" value={Math.round(selectedComponent.posicao_y)} onChange={(e) => updateSelectedDimensions('posicao_y', e.target.value)} className="h-8 bg-zinc-800 border-zinc-700 text-sm" />
            </div>
          </div>
          
          {selectedComponent.ferragens?.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
              <span className="text-xs text-zinc-400">Ferragens:</span>
              {selectedComponent.ferragens.map((f, i) => (
                <Badge key={i} variant="outline" className="text-xs bg-amber-900/30 border-amber-800 text-amber-300">{f}</Badge>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Legend */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400 px-1">
          {Object.entries(COMPONENT_TYPES).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm border border-zinc-600" style={{ backgroundColor: config.color }} />
              <span>{config.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
